import type { CSSProperties, FC, ReactNode } from 'react'

import { Mail, Phone } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { OpenStatus } from '@/components/generals/open-status'
import { SplitWords } from '@/components/generals/split-words'
import { CallbackForm } from '@/components/generals/callback-form'
import { PHOTO } from '@/constants/media'
import { CONTACT } from '@/constants/site'

const PROMISES = [
    'Ekte folk, ikke roboter',
    'Ingen forpliktelser',
    'Svar på det du lurer på'
]

const BliOppringt: FC = (): ReactNode => {
    return (
        <Layout header='dark'>
            <Seo
                title='Bli oppringt'
                description="Legg igjen nummeret ditt, så ringer en ekte person fra Svenn deg tilbake. Hverdager 08 til 16, helt uforpliktende."
            />

            <section id='bli-oppringt' className='tone-night grain relative overflow-hidden' data-header='dark'>
                <div className='grid-lines' />

                <div className='wrap relative grid grid-cols-12 gap-x-6 gap-y-14 pt-[calc(var(--header-h)+4rem)] pb-20 md:pb-28'>
                    <div className='col-span-12 flex flex-col lg:col-span-5'>
                        <p className='t-label load-fade text-tone-muted' style={{ '--load-delay': '0.05s' } as CSSProperties}>Bli oppringt · Helt uforpliktende</p>

                        <h1 className='t-display mt-8 balance'>
                            <span className='load-words block' style={{ '--load-delay': '0.15s' } as CSSProperties}>
                                <SplitWords text='Vi ringer' />
                            </span>
                            <span className='load-words t-serif block text-tone-ink' style={{ '--load-delay': '0.3s' } as CSSProperties}>
                                <SplitWords text='deg.' start={2} />
                            </span>
                        </h1>

                        <div className='load-fade mt-10' style={{ '--load-delay': '0.7s' } as CSSProperties}>
                            <p className='t-lead max-w-md text-tone-muted pretty'>
                                Legg igjen nummeret ditt, så ringer en av oss deg tilbake. Vi svarer på det du lurer på, og viser deg hvordan Svenn passer for akkurat din bedrift.
                            </p>

                            <OpenStatus className='mt-8 text-tone-fg' />

                            <ul className='mt-10 flex flex-col border-t border-tone-line'>
                                {PROMISES.map(promise => (
                                    <li key={promise} className='flex items-center gap-4 border-b border-tone-line py-3.5'>
                                        <span className='h-px w-6 bg-tone-accent' aria-hidden='true' />
                                        {promise}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='load-fade mt-auto hidden pt-14 lg:block' style={{ '--load-delay': '0.9s' } as CSSProperties}>
                            <div className='flex items-center gap-4'>
                                <img src={PHOTO.svennTeam} alt='To smilende medarbeidere fra Svenn i et møte på kontoret' loading='lazy' decoding='async' className='size-20 object-cover' />
                                <p className='text-sm text-tone-muted'>Teamet i Skippergata 9, Oslo, sitter klare.</p>
                            </div>
                        </div>
                    </div>

                    <div className='col-span-12 lg:col-span-6 lg:col-start-7'>
                        <div className='load-fade tone-chalk p-6 shadow-[0_50px_100px_-40px_rgb(0_0_0/0.6)] sm:p-10 md:p-12' style={{ '--load-delay': '0.5s' } as CSSProperties}>
                            <p className='t-label text-slate'>Ring meg tilbake</p>
                            <h2 className='t-h2 mt-3 mb-10'>Når passer det å prate?</h2>
                            <CallbackForm />
                        </div>

                        <div className='mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2'>
                            <a href={CONTACT.phoneHref} className='group flex items-center gap-4 border border-tone-line p-5 transition-colors duration-500 hover:border-tone-accent'>
                                <Phone className='size-5 text-tone-ink' aria-hidden='true' />
                                <span>
                                    <span className='t-label block text-tone-muted'>Heller ringe selv?</span>
                                    <span className='mt-1 block text-lg font-semibold tabular'>{CONTACT.phone}</span>
                                </span>
                            </a>
                            <a href={`mailto:${CONTACT.email}`} className='group flex items-center gap-4 border border-tone-line p-5 transition-colors duration-500 hover:border-tone-accent'>
                                <Mail className='size-5 text-tone-ink' aria-hidden='true' />
                                <span>
                                    <span className='t-label block text-tone-muted'>Skriv til oss</span>
                                    <span className='mt-1 block text-lg font-semibold'>{CONTACT.email}</span>
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default BliOppringt
